/**
 * The permission-driven navigation **surface registry** (ADR-084). Every
 * permission-gated destination is declared here as data, and the whole chrome —
 * the user dropdown's workspace section, the `/admin` console sidebar, the
 * console door, the `/admin` and `/studio` entry redirects — is a pure
 * projection of these declarations. One declaration drives *both* a link's
 * visibility *and* (via {@link canAccessSurface}, mirrored server-side by
 * `requireSurfaceAccess`) its route's read/entry guard, so the two can never
 * drift.
 *
 * This extends the pure-projection pattern of `resolveNavbar`
 * (`app/components/app-shell-nav.ts`): no React, no router, no `t` — the
 * selectors return i18n *keys*, and each chrome consumer translates them in its
 * own namespace. That keeps this module **client-safe** (pure data plus the
 * already client-safe `userHasPermission` / `userHasRole`) and lets the whole
 * permission→surface matrix be pinned by a table-driven unit test.
 *
 * See `docs/agents/nav-surfaces.md` for the "how to add a surface" convention.
 */

import {
	type PermissionString,
	type RoleName,
	userHasPermission,
	userHasRole,
} from './user.ts'
import { type IconName } from '@/icon-name'

/**
 * A **placement** is a real chrome render location the template owns. The set is
 * CLOSED: `workspace` (the user dropdown — things a user *produces/participates
 * in*) and `console` (`/admin` — governance over the platform or other people).
 * Adding a placement means building a new chrome region, a deliberate template
 * change — not something a new surface does.
 */
export type Placement = 'workspace' | 'console'

/**
 * A workspace item's label key, translated in the `common` dictionary (ADR-074)
 * — the user dropdown renders under `common`.
 */
type WorkspaceLabelKey = 'nav.blog'

/**
 * A console item's label key, translated in the `admin` dictionary — the console
 * sidebar renders under `admin` (`shell.nav.*`).
 */
type ConsoleLabelKey =
	| 'shell.nav.blog'
	| 'shell.nav.messages'
	| 'shell.nav.users'
	| 'shell.nav.roles'
	| 'shell.nav.audit'
	| 'shell.nav.cache'
	| 'shell.nav.integrationLogs'

/**
 * A console sidebar section heading key (an `admin`-namespace key). Sections
 * render in registry **declaration order**; surfaces sharing a `group` must be
 * declared contiguously (see {@link consoleGroupsFor}).
 */
type ConsoleGroupKey =
	| 'shell.nav.blog'
	| 'shell.nav.inbox'
	| 'shell.nav.access'
	| 'shell.nav.system'

/**
 * The permission gate half of a surface, encoding the mutual exclusion of the
 * two ways to reveal a surface at the type level: an any-of `permission`
 * (preferred) OR the discouraged `requireRole` escape hatch — never both.
 */
type SurfaceGate =
	| { permission: PermissionString | PermissionString[]; requireRole?: never }
	| { requireRole: RoleName; permission?: never }

type SurfaceBase = {
	id: string
	to: string
	icon: IconName
	/** Active-path prefix; defaults to `to` (see {@link surfaceMatch}). */
	match?: string
}

export type WorkspaceSurface = SurfaceBase & {
	placement: 'workspace'
	labelKey: WorkspaceLabelKey
} & SurfaceGate

export type ConsoleSurface = SurfaceBase & {
	placement: 'console'
	labelKey: ConsoleLabelKey
	/** The sidebar section this surface sits in (console only). */
	group: ConsoleGroupKey
} & SurfaceGate

export type NavSurface = WorkspaceSurface | ConsoleSurface

/**
 * The user shape the gate reads: exactly the roles→permissions projection the
 * root loader selects (`app/root.tsx`), so `useOptionalUser()` feeds these
 * selectors directly. A structural subset satisfying both `userHasPermission`
 * and `userHasRole`.
 */
export type NavUser =
	| {
			roles: Array<{
				name: string
				permissions: Array<{ action: string; entity: string; access: string }>
			}>
	  }
	| null
	| undefined

/**
 * The registry. **Declaration order is meaningful**: it drives the console
 * sidebar's section order and the `first*Surface` redirect targets. The console
 * order below reproduces today's sidebar — content → inbox → access → system —
 * so the migration is behavior-preserving. Re-order the array to re-order the
 * sidebar.
 */
export const navSurfaces: NavSurface[] = [
	// ── workspace (user dropdown) ──────────────────────────────────────────────
	{
		id: 'blog',
		placement: 'workspace',
		to: '/studio/blog',
		// Any-of: an author who can create *or* update posts gets the workspace.
		permission: ['create:post:any', 'update:post:any'],
		labelKey: 'nav.blog',
		icon: 'pencil-2',
	},

	// ── console (/admin), in sidebar-section order ─────────────────────────────
	// content
	{
		id: 'blogAdmin',
		placement: 'console',
		group: 'shell.nav.blog',
		to: '/admin/blog',
		permission: 'read:post:any',
		labelKey: 'shell.nav.blog',
		icon: 'file-text',
	},
	// inbox
	{
		id: 'messages',
		placement: 'console',
		group: 'shell.nav.inbox',
		to: '/admin/messages',
		permission: 'read:contactMessage:any',
		labelKey: 'shell.nav.messages',
		icon: 'envelope-closed',
	},
	// access
	{
		id: 'users',
		placement: 'console',
		group: 'shell.nav.access',
		to: '/admin/users',
		permission: 'read:user:any',
		labelKey: 'shell.nav.users',
		icon: 'avatar',
	},
	{
		id: 'roles',
		placement: 'console',
		group: 'shell.nav.access',
		to: '/admin/roles',
		permission: 'read:role:any',
		labelKey: 'shell.nav.roles',
		icon: 'lock-closed',
	},
	{
		id: 'audit',
		placement: 'console',
		group: 'shell.nav.access',
		to: '/admin/audit',
		permission: 'read:audit:any',
		labelKey: 'shell.nav.audit',
		icon: 'clock',
	},
	// system
	{
		id: 'cache',
		placement: 'console',
		group: 'shell.nav.system',
		to: '/admin/cache',
		// The lone role escape hatch (ADR-084): the cache surface names no
		// permission entity, so it gates on the `admin` role instead. Discouraged —
		// a second such surface is the signal to add a real `system` entity.
		requireRole: 'admin',
		labelKey: 'shell.nav.cache',
		icon: 'clock',
	},
	{
		id: 'integrationLogs',
		placement: 'console',
		group: 'shell.nav.system',
		to: '/admin/integration-logs',
		permission: 'read:integrationLog:any',
		labelKey: 'shell.nav.integrationLogs',
		icon: 'link-2',
	},
]

/** The active-path prefix a surface owns — its explicit `match` or its `to`. */
export function surfaceMatch(surface: NavSurface): string {
	return surface.match ?? surface.to
}

const anyOf = (user: NavUser, perms: PermissionString | PermissionString[]) =>
	(Array.isArray(perms) ? perms : [perms]).some((p) =>
		userHasPermission(user, p),
	)

/**
 * The unified gate — the one rule the nav projection and the server guard share.
 * A surface is accessible when the viewer holds the escape-hatch role (when the
 * surface uses `requireRole`) or **any** of its listed permissions (any-of).
 */
export function canAccessSurface(user: NavUser, surface: NavSurface): boolean {
	return surface.requireRole
		? userHasRole(user ?? null, surface.requireRole)
		: anyOf(user, surface.permission)
}

/** The workspace surfaces a viewer can see, in declaration order. */
export const workspaceSurfacesFor = (user: NavUser): WorkspaceSurface[] =>
	navSurfaces.filter(
		(s): s is WorkspaceSurface =>
			s.placement === 'workspace' && canAccessSurface(user, s),
	)

/** A console sidebar section: its heading key and the surfaces under it. */
export type ConsoleNavGroup = {
	labelKey: ConsoleGroupKey
	surfaces: ConsoleSurface[]
}

/**
 * The console sidebar the viewer can see, as grouped sections in registry
 * declaration order. Consecutive console surfaces sharing a `group` collapse
 * into one section — so section order is declaration order, and a section
 * disappears when the viewer can see none of its surfaces. The consumer
 * translates each `labelKey` in the `admin` namespace.
 */
export function consoleGroupsFor(user: NavUser): ConsoleNavGroup[] {
	const groups: ConsoleNavGroup[] = []
	for (const surface of navSurfaces) {
		if (surface.placement !== 'console' || !canAccessSurface(user, surface)) {
			continue
		}
		const current = groups.at(-1)
		if (current && current.labelKey === surface.group) {
			current.surfaces.push(surface)
		} else {
			groups.push({ labelKey: surface.group, surfaces: [surface] })
		}
	}
	return groups
}

const hasPlacementAccess = (user: NavUser, placement: Placement): boolean =>
	navSurfaces.some((s) => s.placement === placement && canAccessSurface(user, s))

/** Whether the viewer can reach any console surface (drives the console door). */
export const hasConsoleAccess = (user: NavUser): boolean =>
	hasPlacementAccess(user, 'console')

/** Whether the viewer can reach any workspace surface (mirrors {@link hasConsoleAccess}). */
export const hasWorkspaceAccess = (user: NavUser): boolean =>
	hasPlacementAccess(user, 'workspace')

/** Where a bare `/settings/profile` fallback lands when a placement is empty. */
const FALLBACK_SURFACE = '/settings/profile'

const firstSurfaceTo = (user: NavUser, placement: Placement): string =>
	navSurfaces.find((s) => s.placement === placement && canAccessSurface(user, s))
		?.to ?? FALLBACK_SURFACE

/**
 * The `/admin` entry target: the first console surface the viewer can see, or
 * `/settings/profile` when they can see none (so a direct `/admin` hit bounces
 * home instead of 403-ing).
 */
export const firstConsoleSurface = (user: NavUser): string =>
	firstSurfaceTo(user, 'console')

/** The `/studio` entry target, mirroring {@link firstConsoleSurface}. */
export const firstWorkspaceSurface = (user: NavUser): string =>
	firstSurfaceTo(user, 'workspace')
